import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formsApi } from '@/api/forms';
import { queryKeys } from '@/lib/queryKeys';
import { ApiError } from '@/api/client';

export function FormEditPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const { data } = useQuery({
    queryKey: queryKeys.forms.detail(formId!),
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  });

  const mutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => formsApi.update(formId!, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.forms.detail(formId!) });
      qc.invalidateQueries({ queryKey: queryKeys.forms.all });
      navigate(`/forms/${formId}`);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Falha ao atualizar'),
  });

  const form = data?.form;
  if (!form) return <div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-1/3" /></div>;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    mutation.mutate({
      name: fd.get('name'),
      allowedDomains: String(fd.get('allowedDomains') || '*').split(',').map((s) => s.trim()).filter(Boolean),
      notificationEmails: String(fd.get('notificationEmails') || '').split(',').map((s) => s.trim()).filter(Boolean),
      submissionBehavior: fd.get('submissionBehavior'),
      redirectUrl: fd.get('redirectUrl') || null,
      thankYouMessage: fd.get('thankYouMessage') || null,
      webhookUrl: fd.get('webhookUrl') || null,
      honeypotField: fd.get('honeypotField') || '_gotcha',
      isActive: fd.get('isActive') === 'true',
    });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Editar: {form.name}</h1>
      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Formulario *</label>
          <input name="name" required defaultValue={form.name} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dominios Permitidos</label>
          <input name="allowedDomains" defaultValue={form.allowedDomains.join(', ')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">E-mails de Notificacao</label>
          <input name="notificationEmails" defaultValue={form.notificationEmails.join(', ')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apos Envio</label>
          <select name="submissionBehavior" defaultValue={form.submissionBehavior} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50">
            <option value="thank_you_page">Mostrar Pagina de Agradecimento</option>
            <option value="redirect">Redirecionar para URL</option>
            <option value="json">Apenas Resposta JSON</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL de Redirecionamento</label>
          <input name="redirectUrl" type="url" defaultValue={form.redirectUrl ?? ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Agradecimento</label>
          <textarea name="thankYouMessage" rows={2} defaultValue={form.thankYouMessage ?? ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL do Webhook</label>
          <input name="webhookUrl" type="url" defaultValue={form.webhookUrl ?? ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Campo Honeypot</label>
          <input name="honeypotField" defaultValue={form.honeypotField} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" name="isActive" value="true" defaultChecked={form.isActive} id="isActive" className="rounded" />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Ativo</label>
        </div>

        <button type="submit" disabled={mutation.isPending}
          className="w-full py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50">
          {mutation.isPending ? 'Salvando...' : 'Salvar Alteracoes'}
        </button>
      </form>
    </div>
  );
}
